import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Observable, combineLatest, map, shareReplay } from 'rxjs';

import { CorporateReportingStoreService } from '../../../services/stores/corporate-reporting-store.service';
import { HighRiskHotelsTableComponent } from '../high-risk-hotels-table/high-risk-hotels-table.component';
import { HighRiskReportsListComponent } from '../high-risk-reports-list/high-risk-reports-list.component';

import { CorporatePropertyAiSummary } from '../../../domain/Ai/corporate-property-ai-summary';
import { RiskLevel } from '../../../domain/corporate/risk-level';
import { documentTypeToLabel } from '../../../util/ai-document-type-utils';

// use the daily thresholds (500 / 151 / 150)
import { DEFAULT_HOTEL_RISK_AMOUNT_THRESHOLDS } from '../../../domain/corporate/hotel-risk-amount-thresholds';

import {
   HighRiskHotelReportItem,
   HighRiskHotelRowVm
} from '../../../domain/corporate/high-risk-hotel-row-vm';

@Component({
   selector: 'app-high-risk-section',
   standalone: true,
   imports: [
      CommonModule,
      HighRiskHotelsTableComponent,
      HighRiskReportsListComponent
   ],
   templateUrl: './high-risk-section.component.html',
   styleUrls: ['./high-risk-section.component.scss']
})
export class HighRiskSectionComponent {
   loading$!: Observable<boolean>;
   errorStatus$!: Observable<number | null>;

   highRiskHotels$!: Observable<HighRiskHotelRowVm[]>;
   daysInRange$!: Observable<number>;

   constructor(private store: CorporateReportingStoreService) {
      this.loading$ = this.store.loading$;
      this.errorStatus$ = this.store.errorStatus$;

      this.daysInRange$ = this.store.daysInRange$;

      // IMPORTANT: status should be based on avg/day (total / days)
      this.highRiskHotels$ = combineLatest([
         this.store.filteredData$,
         this.daysInRange$
      ]).pipe(
         map(([rows, days]) => this.buildHighRiskHotelRows(rows, days)),
         shareReplay({ bufferSize: 1, refCount: true })
      );
   }

   private buildHighRiskHotelRows(
      rows: CorporatePropertyAiSummary[],
      daysInRange: number
   ): HighRiskHotelRowVm[] {
      const days = Math.max(daysInRange ?? 0, 1);

      return rows
         .map((r) => {
            const propertyId = r.property?.id ?? 0;
            const hotelName = r.property?.name ?? 'Unknown';

            const allReportItems = this.buildHotelReportItems(r); // NEW

            const uniqueItems =
               this.uniqueReportItemsByTaskInstanceId(allReportItems); // NEW

            const reportsAtRisk = uniqueItems.length;

            const financialImpact = uniqueItems
               .filter((x) => x.isFinancial)
               .reduce((sum, x) => sum + (x.amount ?? 0), 0);

            const avgDaily = financialImpact / days;
            const status = this.getRiskLevelByAvgDaily(avgDaily);
            const statusRank = status === 'High' ? 3 : status === 'Medium' ? 2 : 1;

            const financialReports = uniqueItems
               .filter((x) => x.isFinancial)
               .sort(
                  (a, b) =>
                     (b.date ?? '').localeCompare(a.date ?? '') ||
                     Math.abs(b.amount) - Math.abs(a.amount)
               ); // NEW

            const operationalReports = uniqueItems
               .filter((x) => !x.isFinancial)
               .sort(
                  (a, b) =>
                     (b.date ?? '').localeCompare(a.date ?? '') ||
                     Math.abs(b.amount) - Math.abs(a.amount)
               ); // NEW

            return {
               propertyId,
               hotelName,
               reportsAtRisk,
               financialImpact,
               status,
               statusRank,
               financialReports,
               operationalReports
            } satisfies HighRiskHotelRowVm;
         })
         .sort(
            (a, b) =>
               b.statusRank - a.statusRank ||
               b.financialImpact - a.financialImpact ||
               b.reportsAtRisk - a.reportsAtRisk ||
               (a.hotelName ?? '').localeCompare(b.hotelName ?? '')
         );
   }

   private buildHotelReportItems(
      row: CorporatePropertyAiSummary
   ): HighRiskHotelReportItem[] {
      const items: HighRiskHotelReportItem[] = [];

      for (const day of row.summaryResponses ?? []) {
         const date = (day as any)?.date ?? null;
         if (!date) continue;

         for (const m of (day as any)?.modelSummaries ?? []) {
            if (m?.hasError) continue;

            const isFinancial = !!m?.isFinancial;
            const amount = isFinancial
               ? (m?.amount ?? 0)
               : this.getNonFinancialCount(m);

            items.push({
               date,
               reportName: this.getModelDisplayName(m),
               amount,
               taskInstanceId:
                  typeof m?.taskInstanceId === 'number'
                     ? m.taskInstanceId
                     : undefined,
               isFinancial
            });
         }
      }

      return items;
   }

   private getModelDisplayName(m: any): string {
      const dt = m?.documentType;
      if (typeof dt === 'number') {
         const label = documentTypeToLabel(dt);
         if (label !== 'Unknown') return label;
      }

      //Fallbacks
      return (
         (m?.reportName ??
            m?.taskName ??
            m?.taskTemplateName ??
            m?.taskTemplateDisplayName ??
            m?.modelName ??
            m?.name ??
            'Unknown Report') as string
      ).trim();
   }

   private uniqueReportItemsByTaskInstanceId(
      items: HighRiskHotelReportItem[]
   ): HighRiskHotelReportItem[] {
      const byId = new Map<number, HighRiskHotelReportItem>();
      const withoutId: HighRiskHotelReportItem[] = [];

      for (const item of items) {
         const id = item.taskInstanceId;
         if (!id) {
            withoutId.push(item);
            continue;
         }

         const existing = byId.get(id);
         if (!existing) {
            byId.set(id, item);
            continue;
         }

         const exDate = existing.date ?? '';
         const itDate = item.date ?? '';

         if (itDate > exDate) {
            byId.set(id, item);
         } else if (
            itDate === exDate &&
            Math.abs(item.amount ?? 0) > Math.abs(existing.amount ?? 0)
         ) {
            byId.set(id, item);
         }
      }

      return [...Array.from(byId.values()), ...withoutId];
   }

   private getNonFinancialCount(model: any): number {
      if (Array.isArray(model?.summaryData)) return model.summaryData.length;
      return model?.amount ?? 0;
   }

   private getRiskLevelByAvgDaily(avgDailyAmount: number): RiskLevel {
      const t = DEFAULT_HOTEL_RISK_AMOUNT_THRESHOLDS;
      if (avgDailyAmount >= t.highMin) return 'High';
      if (avgDailyAmount >= t.mediumMin) return 'Medium';
      return 'Low';
   }
}
